<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useTaxPackExportModal } from '@/composables/useTaxPackExportModal'
import { useManualGivingDonations } from '@/composables/useManualGivingDonations'
import { useDashboardToast } from '@/composables/useDashboardToast'
import { downloadTaxPackPdf } from '@/helpers/downloadTaxPackPdf'
import DashboardModal from '@/components/dashboard/ui/DashboardModal.vue'

const { t } = useI18n()
const store = useStore()
const { showToast } = useDashboardToast()
const { manualDonations, loadManualDonations } = useManualGivingDonations()

const {
  isOpen,
  pendingContext,
  detailLevel,
  closeTaxPackExportModal,
  resolveExportOptions,
} = useTaxPackExportModal()

const isDownloading = ref(false)

const onDownload = async () => {
  const context = pendingContext.value
  if (!context || isDownloading.value) return

  isDownloading.value = true
  try {
    await loadManualDonations()
    const exportOptions = resolveExportOptions()
    await downloadTaxPackPdf(context.period, exportOptions, {
      statements: store.getters.userStatements ?? [],
      financialYears: store.getters.financialYears ?? [],
      manualDonations: manualDonations.value,
      fallbackDonor: store.getters.user?.details ?? null,
    })
    showToast(t('Dashboard.Toast.TaxPackDownloaded'))
    closeTaxPackExportModal()
  } catch (error) {
    console.error('Statement PDF download failed', error)
    showToast(t('Dashboard.Toast.TaxPackDownloadFailed'))
  } finally {
    isDownloading.value = false
  }
}
</script>

<template>
  <DashboardModal
    :is-open="isOpen && Boolean(pendingContext)"
    modal-class="sa-tax-pack-export-modal"
    aria-labelledby="tax-pack-export-title"
    :close-disabled="isDownloading"
    @close="closeTaxPackExportModal"
  >
    <template v-if="pendingContext">
      <div class="sa-modal-icon sa-modal-icon--blue" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path
            d="M12 10v6m0 0l-3-3m3 3l3-3M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1M16 10V5a2 2 0 00-2-2H10a2 2 0 00-2 2v5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>
      <h2 id="tax-pack-export-title">{{ $t('Dashboard.Statements.TaxPackExport.Title') }}</h2>
      <p class="sa-tax-pack-export-modal__intro">
        {{ $t('Dashboard.Statements.TaxPackExport.Intro', { year: pendingContext.taxYearTitle }) }}
      </p>

      <fieldset class="sa-tax-pack-export-fieldset">
        <legend class="sa-tax-pack-export-legend">
          {{ $t('Dashboard.Statements.TaxPackExport.DetailLegend') }}
        </legend>
        <label class="sa-tax-pack-export-option">
          <input
            v-model="detailLevel"
            type="radio"
            value="basic"
            name="tax-pack-detail"
            :disabled="isDownloading"
          />
          <span>
            <span class="sa-tax-pack-export-option__title">
              {{ $t('Dashboard.Statements.TaxPackExport.Basic.Title') }}
            </span>
            <span class="sa-tax-pack-export-option__sub">
              {{ $t('Dashboard.Statements.TaxPackExport.Basic.Sub') }}
            </span>
          </span>
        </label>
        <label class="sa-tax-pack-export-option">
          <input
            v-model="detailLevel"
            type="radio"
            value="detailed"
            name="tax-pack-detail"
            :disabled="isDownloading"
          />
          <span>
            <span class="sa-tax-pack-export-option__title">
              {{ $t('Dashboard.Statements.TaxPackExport.Detailed.Title') }}
            </span>
            <span class="sa-tax-pack-export-option__sub">
              {{ $t('Dashboard.Statements.TaxPackExport.Detailed.Sub') }}
            </span>
          </span>
        </label>
      </fieldset>
    </template>

    <template v-if="pendingContext" #footer>
      <div class="sa-modal-actions">
        <button
          type="button"
          class="sa-btn-secondary"
          :disabled="isDownloading"
          @click="closeTaxPackExportModal"
        >
          {{ $t('Dashboard.Statements.TaxPackExport.Cancel') }}
        </button>
        <button
          type="button"
          class="sa-btn-primary sa-modal-actions__primary"
          :disabled="isDownloading"
          @click="onDownload"
        >
          {{
            isDownloading
              ? $t('Dashboard.Statements.TaxPackExport.Downloading')
              : $t('Dashboard.Statements.TaxPackExport.Download')
          }}
        </button>
      </div>
    </template>
  </DashboardModal>
</template>
